import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { DoodleIcon, directionIcons } from "./DoodleIcon";
import { CourseCard } from "./CourseCard";
import { courses, courseCategories } from "../data/mock";
import "./course-filters.css";

export const CoursesSection = () => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [activeCategory, setActiveCategory] = useState("Все");
  const tabRefs = useRef([]);
  const animateSelection = useRef(false);
  const selectionAnimation = useRef(null);
  const directions = ["Все", ...courseCategories.filter(category => !["С нуля", "Продвинутый уровень"].includes(category))];
  const labels = {"Искусственный интеллект": "Нейросети", "CMS и Конструкторы": "CMS и конструкторы", "Веб Дизайн": "Веб-дизайн", "Веб разработка": "Веб-разработка"};
  const selectTab = (index) => {
    animateSelection.current = false;
    setActiveCategory(directions[index]);
    tabRefs.current[index]?.focus();
    tabRefs.current[index]?.scrollIntoView({block: "nearest", inline: "nearest", behavior: "instant"});
  };
  const handleTabKey = (event, index) => {
    let next;
    if (event.key === "ArrowRight") next = (index + 1) % directions.length;
    if (event.key === "ArrowLeft") next = (index - 1 + directions.length) % directions.length;
    if (event.key === "Home") next = 0;
    if (event.key === "End") next = directions.length - 1;
    if (next !== undefined) { event.preventDefault(); selectTab(next); }
  };

  const bestsellersOrder = ["ai-course", "figma", "html-css", "javascript", "photoshop", "tilda"];

  const filteredCourses = courses.filter(course =>
    !course.hidden &&
    (activeCategory === "Все" || course.category === activeCategory ||
    (course.additionalCategories && course.additionalCategories.includes(activeCategory)))
  ).sort((a, b) => {
    if (activeCategory === "Бестселлеры") {
      return bestsellersOrder.indexOf(a.slug) - bestsellersOrder.indexOf(b.slug);
    }
    return 0;
  });

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 10);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scroll = (dir) => {
    if (scrollRef.current) {
      const distance = Math.max(340, scrollRef.current.clientWidth * 0.8);
      scrollRef.current.scrollBy({ left: dir === "left" ? -distance : distance,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? "instant" : "smooth" });
    }
  };

  useEffect(() => {
    const track = scrollRef.current;
    if (!track) return;
    track.scrollLeft = 0;
    selectionAnimation.current?.cancel();
    if (animateSelection.current && !window.matchMedia('(prefers-reduced-motion: reduce)').matches && track.animate) {
      selectionAnimation.current = track.animate([{opacity:.55,transform:'translateY(6px)'},{opacity:1,transform:'translateY(0)'}],
        {duration:200,easing:'cubic-bezier(0.23, 1, 0.32, 1)'});
    }
    animateSelection.current = false;
    const observer = new ResizeObserver(checkScroll);
    observer.observe(track);
    const frame = requestAnimationFrame(checkScroll);
    return () => { observer.disconnect(); cancelAnimationFrame(frame); selectionAnimation.current?.cancel(); };
  }, [activeCategory]);

  return (
    <section id="courses" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="course-section-heading">
          <div>
            <p className="course-eyebrow">Все курсы</p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight">Популярные курсы</h2>
          </div>
        </div>

        <div className="course-filter-bar">
          <div className="course-filter-options">
            <label className="mobile-category-select">Направление
              <select value={activeCategory} onChange={event => { animateSelection.current = false; setActiveCategory(event.target.value); }}>
                {directions.map(category => <option key={category} value={category}>{labels[category] || category}</option>)}
              </select>
            </label>
            <div className="course-direction-tabs" role="tablist" aria-label="Направление обучения">
              {directions.map((category, index) => (
                <button key={category} ref={element => { tabRefs.current[index] = element; }}
                  id={`course-tab-${index}`} role="tab" aria-selected={activeCategory === category}
                  aria-controls="course-results" tabIndex={activeCategory === category ? 0 : -1}
                  onClick={event => { animateSelection.current = event.detail > 0; setActiveCategory(category); }} onKeyDown={event => handleTabKey(event, index)}
                  data-testid={`category-filter-${category.toLowerCase().replace(/\s+/g, '-')}`}
                  className="course-direction-tab">
                  <DoodleIcon name={directionIcons[category] || "book"} /><span className="course-direction-label">{labels[category] || category}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="course-results-toolbar">
          <div className="course-results-navigation">
          <p role="status" aria-live="polite">{filteredCourses.length} {filteredCourses.length === 1 ? "курс" : filteredCourses.length < 5 ? "курса" : "курсов"}</p>
          <div className="course-scroll-buttons">
            <button aria-label="Предыдущие курсы" onClick={() => scroll("left")} disabled={!canScrollLeft} className="course-scroll-button"><ArrowLeft size={24} aria-hidden="true" /></button>
            <button aria-label="Следующие курсы" onClick={() => scroll("right")} disabled={!canScrollRight} className="course-scroll-button"><ArrowRight size={24} aria-hidden="true" /></button>
          </div>
          </div>
        </div>

        <div
          id="course-results" role="tabpanel" aria-labelledby={`course-tab-${directions.indexOf(activeCategory)}`} tabIndex={0}
          ref={scrollRef}
          onScroll={checkScroll}
          className="flex gap-6 overflow-x-auto pb-4 scroll-smooth hide-scrollbar"
        >
          {filteredCourses.length > 0 ? (
            filteredCourses.map((course, index) => (
              <CourseCard key={course.id} course={course} index={index} carousel />
            ))
          ) : (
            <div className="w-full py-12 text-center">
              <p className="text-gray-500 text-lg">В этом направлении пока нет курсов.</p>
              <Link className="course-empty-reset" to="/courses">Открыть каталог</Link>
            </div>
          )}
        </div>
        <Link to="/courses" className="course-catalog-link courses-bottom-link"><span className="course-catalog-text">Посмотреть все курсы</span><ArrowRight size={18} /></Link>
      </div>
    </section>
  );
};
